import { isFrontToast, showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import { openDownloadUrl } from "@/lib/browser/download";

import {
  EXPORT_HELD_READY_TOAST_MS,
  EXPORT_RESULT_TOAST_MS,
  MY_EXPORTS_HREF,
} from "./config";
import { slowExportJobCopy } from "./label";
import { isListedInMyExports } from "./machine";
import type { ExportJobRefusal } from "./store";
import type { TrackedExportJob } from "./types";

/**
 * The tracker's toasts: a view of job transitions, never owned by a page.
 * Each reads its job: where to show, whether My Exports lists it (else the
 * copy doesn't point there), whether the user can export less. A job's
 * result takes over its loading toast while that is up in front.
 */

type Navigate = (href: string) => void;

/** A tracked job, or the request of a start the tracker refused. */
type ToastJob = Pick<TrackedExportJob, "adapter"> &
  Partial<Pick<TrackedExportJob, "reducible">>;

const ALSO_IN_MY_EXPORTS = " It will also be in My Exports.";

/**
 * Jobs whose loading toast went down. A swipe reports it before sonner lets
 * go of the toast, and a settled job is no longer in the store to note it.
 */
const closedNotices = new Set<string>();

/**
 * Where a job's result goes: over its loading toast while that is up in
 * front. An update keeps its place in sonner's stack, so under a newer toast
 * the result would go unseen: that loading toast makes way instead.
 */
function slot(job: TrackedExportJob) {
  const closed = closedNotices.delete(job.key);
  const { noticeId } = job;
  if (noticeId === null || closed) return {};
  if (isFrontToast(noticeId)) return { id: noticeId };
  showToast.dismiss(noticeId);
  return {};
}

function listed(job: ToastJob) {
  return isListedInMyExports(job.adapter);
}

function whenReady(job: TrackedExportJob) {
  const when =
    job.adapter.readyAction === "download"
      ? "We'll download it automatically when it's ready."
      : "We'll let you know when it's ready.";
  return listed(job) ? when + ALSO_IN_MY_EXPORTS : when;
}

/**
 * The job's one toast from accepted to settled, updated in place past the
 * budget and then to its result. Closing it only closes it: `onClose`.
 */
export function showExportPreparingToast(job: TrackedExportJob, onClose: () => void) {
  const next = job.slow ? slowExportJobCopy(job.adapter) : whenReady(job);
  return showToast.loading("Preparing export", {
    description: `${job.label}. ${next}`,
    duration: Infinity,
    onDismiss: () => {
      closedNotices.add(job.key);
      onClose();
    },
    ...(job.noticeId === null ? {} : { id: job.noticeId }),
  });
}

export function showExportRefusedToast(reason: ExportJobRefusal, job: ToastJob) {
  if (reason === "busy") {
    const still = "This export is still preparing.";
    showToast.info("Export already in progress", {
      description: listed(job) ? still + ALSO_IN_MY_EXPORTS : still,
    });
  } else {
    showToast.info("Too many exports in progress", {
      description: "Wait for one to finish, then try again.",
    });
  }
}

export function showExportStartErrorToast(error: unknown) {
  showToast.error("Export failed", {
    description: getApiErrorMessage(error, "Could not start export."),
  });
}

export type ExportReadyDelivery = "downloaded" | "undelivered" | "notify";

const READY_COPY: Record<ExportReadyDelivery, { title: string; description: string }> = {
  downloaded: {
    title: "Export complete",
    description: "Your file has been downloaded.",
  },
  undelivered: {
    title: "Export ready",
    description: "We couldn't download it automatically. Select Download to save it.",
  },
  notify: {
    title: "Export ready",
    description: "Your file is ready. It's also in My Exports.",
  },
};

const UNLISTED_NOTIFY_COPY = {
  title: "Export ready",
  description: "Your file is ready. Select Download to save it.",
};

/**
 * The Download action is the user-gesture fallback when auto-download can't
 * run. A file still to be saved that My Exports doesn't list stays until
 * dismissed: nothing else leads back to it.
 */
export function showExportReadyToast(
  job: TrackedExportJob,
  delivery: ExportReadyDelivery,
  url: string,
  { note }: { note?: string | null } = {},
) {
  const unlisted = !listed(job);
  const held = unlisted && (delivery === "notify" || delivery === "undelivered");
  const { title, description } =
    unlisted && delivery === "notify" ? UNLISTED_NOTIFY_COPY : READY_COPY[delivery];
  showToast.success(title, {
    description: note ? `${description} ${note}` : description,
    duration: held ? EXPORT_HELD_READY_TOAST_MS : EXPORT_RESULT_TOAST_MS,
    action: { label: "Download", onClick: () => openDownloadUrl(url) },
    ...slot(job),
  });
}

export function showExportFailedToast(
  job: TrackedExportJob,
  error: string | null,
  navigate: Navigate,
) {
  const isCredits = !!error?.toLowerCase().includes("insufficient credits");
  const topUp = job.reducible
    ? "Add credits or reduce the export size."
    : "Add credits and try again.";
  showToast.error(isCredits ? "Not enough credits" : "Export failed", {
    description: isCredits
      ? `You don't have enough credits for this export. ${topUp}`
      : "Something went wrong. Please try again.",
    duration: EXPORT_RESULT_TOAST_MS,
    action: myExportsAction(job, navigate),
    ...slot(job),
  });
}

export function showExportLostToast(job: TrackedExportJob, navigate: Navigate) {
  showToast.info("We couldn't track this export", {
    description: listed(job)
      ? "It will appear in My Exports if it finishes."
      : "Please try again.",
    duration: EXPORT_RESULT_TOAST_MS,
    action: myExportsAction(job, navigate),
    ...slot(job),
  });
}

export function showExportTimedOutToast(job: TrackedExportJob, navigate: Navigate) {
  // The start never answered: there may be no export at all.
  if (job.phase === "starting") {
    showToast.info("Export may not have started", {
      description: listed(job)
        ? "Check My Exports before trying again."
        : "Please try again in a few minutes.",
      duration: EXPORT_RESULT_TOAST_MS,
      action: myExportsAction(job, navigate),
      ...slot(job),
    });
    return;
  }
  if (!listed(job)) {
    showToast.info("Export is taking longer than usual", {
      description: "We stopped waiting for it. Please try again in a few minutes.",
      duration: EXPORT_RESULT_TOAST_MS,
      ...slot(job),
    });
    return;
  }
  showToast.info("Still preparing your export", {
    description: "It's taking longer than usual. It will appear in My Exports when it's ready.",
    duration: EXPORT_RESULT_TOAST_MS,
    action: myExportsAction(job, navigate),
    ...slot(job),
  });
}

function myExportsAction(job: ToastJob, navigate: Navigate) {
  return listed(job)
    ? { label: "My Exports", onClick: () => navigate(MY_EXPORTS_HREF) }
    : undefined;
}
